<template>
  <ConsultationBanner :bg-color="colors.background">
    <template #consultationPill>
      <DirectConsultationTypeChip
        :consultation-type="consultation.urgency"
        variant="outlined"
        dark
        class="w-fit"
      />
    </template>

    <template #mainText>
      <div
        :class="[
          consultation.urgency == UrgencyDTO.EMERGENCY
            ? 'flex-col sm:flex-row md:gap-4'
            : 'flex-col',
          `text-${colors.foreground} font-semibold flex`
        ]"
      >
        <ConsultationTimer
          v-if="consultation.nextEscalationAt && isUrgencyWithCountdown(consultation.urgency)"
          class="mr-1 font-semibold"
          dark
          regular
          :show-icon="false"
          :next-escalation-at="consultation.nextEscalationAt"
          :urgency="consultation.urgency"
          :timer-mode="'down'"
          :prefix="t('Still')"
          :suffix="t('left to join')"
        />

        <div>
          <p
            v-if="!shouldRenderSubText() && consultation.question"
            data-testid="main-text-slot-question"
            class="line-clamp-3"
          >
            <span class="font-semibold">{{ t('Question') }}:</span>
            {{ consultation.question }}
          </p>

          <p
            v-if="consultation.urgency === UrgencyDTO.EMERGENCY"
            data-testid="emergency-patient-data"
          >
            {{ renderEmergencyConsultationData() }}
          </p>
        </div>
      </div>
    </template>

    <template v-if="shouldRenderSubText()" #subText>
      <p
        v-if="consultation.question"
        data-testid="sub-text-slot-question"
        :class="`text-${colors.foreground} font-normal line-clamp-3 max-sm:mt-1`"
      >
        <span class="font-semibold">{{ t('Question') }}:</span>
        {{ consultation.question }}
      </p>
    </template>

    <template #actions>
      <VBtn
        v-if="consultation.urgency !== UrgencyDTO.EMERGENCY"
        data-testid="consultations-overview-button"
        variant="text"
        class="button-large pa-0"
        :class="`text-${colors.foreground}`"
        :to="{ name: routeNames.CONSULTATIONS }"
        :active="false"
      >
        {{ t('Open consultation overview') }}
      </VBtn>
      <template v-for="action in consultationActionsPerStatus" :key="action.type">
        <VBtn
          v-if="action.showAction"
          :data-testid="`action-${action.type}`"
          :variant="setButtonVariant(action.type)"
          :text="action.actionName"
          :color="colors.foreground"
          class="text-sm font-semibold bg-white"
          :loading="action.isLoading"
          :disabled="action.disable"
          @click="action.actionCallback"
        />
      </template>
    </template>
  </ConsultationBanner>
</template>

<script setup lang="ts">
import ConsultationBanner from '@/components/consultationrequest/ConsultationBanner.vue'
import { type ConsultationRequestDTO, UrgencyDTO } from '@/generated'
import { isUrgencyWithCountdown } from '@/types/UrgencyWithCountdown'
import ConsultationTimer from '@/components/consultationrequest/ConsultationTimer.vue'
import DirectConsultationTypeChip from '@/atoms/DirectConsultationTypeChip.vue'
import { useI18n } from 'vue-i18n'
import { computed, toRef } from 'vue'
import useConsultationActions from '@/composables/useConsultationActions'
import { routeNames } from '@/router/routeNames'
import { ActionType } from '@/enums/ActionType'
import { translateFullGenderDTO } from '@/util/translations'
import { differenceInYears } from 'date-fns'

interface Props {
  consultation: ConsultationRequestDTO
}

const { t } = useI18n()

const { consultation } = defineProps<Props>()

const reactiveConsultation = toRef(() => consultation)

const { consultationActionsPerStatus } = useConsultationActions(reactiveConsultation)

function renderEmergencyConsultationData(): string {
  const { firstName, lastName, gender, dateOfBirth } = consultation.case.patient

  return (
    t('Patient') +
    `: ${firstName} ${lastName} (${translateFullGenderDTO(gender, t).toLowerCase()}, ${dateOfBirth ? differenceInYears(new Date(), dateOfBirth) : ''})`
  )
}

function shouldRenderSubText(): boolean {
  const { urgency, nextEscalationAt } = consultation
  return !!nextEscalationAt && urgency !== UrgencyDTO.EMERGENCY
}

function setButtonVariant(actionType: ActionType) {
  if (actionType === ActionType.DECLINE) {
    return 'outlined'
  }

  return 'flat'
}

const colors = computed(() => {
  switch (consultation.urgency) {
    case UrgencyDTO.EMERGENCY:
      return {
        background: 'error-50',
        foreground: 'error-700'
      }
    case UrgencyDTO.URGENT:
      return {
        background: 'warning-50',
        foreground: 'warning-700'
      }
    case UrgencyDTO.ROUTINE:
    default:
      return {
        background: 'primary-50',
        foreground: 'primary-700'
      }
  }
})
</script>

<style scoped></style>
